import { useEffect, useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getOrderIssues, getAdminSupportTickets } from "../api/client";
import { listenForAdminIssues, listenForAdminSupport } from "../api/socket";
import FastSendLogo from "./FastSendLogo";

// `permission: null` means always visible to both admin and support (no
// togglable gate — e.g. ticket/claims handling is inherently a support
// duty); `permission: "admin-only"` means the tab never shows for support at
// all (pricing, admin accounts), regardless of what's in its permissions
// list. Everything else must appear in the support account's own
// `permissions` (see models/Admin.SUPPORT_PERMISSIONS).
const ADMIN_TABS = [
  { to: "/admin/riders",  icon: "🛵", label: "Fleet",    permission: "riders"  },
  { to: "/admin/users",   icon: "👥", label: "Users",    permission: "users"   },
  { to: "/admin/orders",  icon: "📦", label: "Orders",   permission: "orders"  },
  { to: "/admin/issues",  icon: "🚨", label: "Issues",   permission: "orders"  },
  { to: "/admin/support", icon: "📨", label: "Support",  permission: null      },
  { to: "/admin/popups",  icon: "📢", label: "Popups",   permission: "popups"  },
  { to: "/admin/revenue", icon: "💰", label: "Revenue",  permission: "revenue" },
  { to: "/admin/pricing", icon: "⚙️", label: "Pricing",  permission: "admin-only" },
  { to: "/admin/map-providers", icon: "🗺️", label: "Maps", permission: "admin-only" },
  { to: "/admin/claims",  icon: "🛡️", label: "Claims",   permission: null      },
  { to: "/admin/admins",  icon: "🔑", label: "Admins",   permission: "admin-only" },
  { to: "/admin/guide",   icon: "📖", label: "Guide",    permission: null      },
];

export default function AdminNav() {
  const { logout, account } = useAuth();
  const navigate = useNavigate();
  const isSuperOrFullAdmin = ["admin", "super_admin"].includes(account?.role);
  const isSupport = account?.role === "support";
  const visibleTabs = ADMIN_TABS.filter(({ permission }) => {
    if (isSuperOrFullAdmin) return true; // super/full admin sees everything
    if (permission === "admin-only") return false;
    if (permission === null) return true;
    return account?.permissions?.includes(permission);
  });
  const [openIssueCount, setOpenIssueCount] = useState(0);
  const [openTicketCount, setOpenTicketCount] = useState(0);

  useEffect(() => {
    const refreshIssues = () => getOrderIssues().then(({ data }) => setOpenIssueCount(data.length)).catch(() => {});
    const refreshTickets = () => getAdminSupportTickets("open").then(({ data }) => setOpenTicketCount(data.length)).catch(() => {});
    refreshIssues();
    refreshTickets();
    const unsubscribeIssues = listenForAdminIssues(refreshIssues);
    const unsubscribeSupport = listenForAdminSupport(refreshTickets);
    return () => {
      unsubscribeIssues();
      unsubscribeSupport();
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <>
      {/* ── Mobile-only floating logout — a sibling of <nav>, not a child:
          .app-nav has backdrop-filter, which creates a new containing block
          for fixed-position descendants, so a fixed button inside it pins to
          the nav's own box instead of the viewport. ── */}
      <button type="button" className="app-nav-mobile-logout" onClick={handleLogout} aria-label="Log out">
        🚪
      </button>
      <nav className="app-nav">
      {/* ── Brand & Admin Profile (sidebar only) ── */}
      <div className="app-nav-brand">
        <Link to="/admin/riders" className="app-nav-logo-link" style={{ display: "inline-block", padding: "4px 0" }}>
          <FastSendLogo variant="horizontal" size="small" />
        </Link>
        {account && (
          <div className="app-nav-user">
            <div className="app-nav-avatar">
              {account.name?.charAt(0)?.toUpperCase() || "A"}
            </div>
            <div className="app-nav-user-info">
              <span className="app-nav-user-name">{account.name}</span>
              <span className="app-nav-user-role" style={{ color: "var(--brand-orange-light)", fontWeight: 700 }}>{isSupport ? "Support" : "Administrator"}</span>
            </div>
          </div>
        )}
      </div>

      {/* ── Nav Items ── */}
      <div className="app-nav-items">
        {visibleTabs.map(({ to, icon, label }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `app-nav-item${isActive ? " active" : ""}`
            }
          >
            <span className="app-nav-item-icon">{icon}</span>
            <span className="app-nav-item-label">
              {label}
              {to === "/admin/issues" && openIssueCount > 0 ? ` (${openIssueCount})` : ""}
              {to === "/admin/support" && openTicketCount > 0 ? ` (${openTicketCount})` : ""}
            </span>
          </NavLink>
        ))}
      </div>

      {/* ── Footer / Logout (sidebar only) ── */}
      <div className="app-nav-footer">
        <button className="app-nav-item app-nav-logout" onClick={handleLogout}>
          <span className="app-nav-item-icon">🚪</span>
          <span className="app-nav-item-label">Log out</span>
        </button>
      </div>
      </nav>
    </>
  );
}
